// Mes Avis Marketplace
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Package, ThumbsUp, MessageCircle, Edit, Trash2, ArrowLeft, X, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  return (
    localStorage.getItem('ecosystem_token') ||
    localStorage.getItem('social_token') ||
    ''
  );
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function mapEnvoye(doc) {
  return {
    id: doc._id || doc.id,
    boutique_slug: doc.boutique_slug || '',
    productTitle: doc.product_name || doc.boutique_name || 'Produit',
    productImage: doc.product_image || null,
    seller: doc.boutique_name || '',
    rating: doc.rating || 0,
    comment: doc.comment || '',
    date: formatDate(doc.created_at),
    helpful: doc.helpful_count || 0,
    sellerResponse: doc.vendor_response || null,
  };
}

function mapRecu(doc) {
  return {
    id: doc._id || doc.id,
    boutique_slug: doc.boutique_slug || '',
    productTitle: doc.product_name || doc.boutique_name || 'Produit',
    productImage: doc.product_image || null,
    buyer: doc.author_name || 'Acheteur',
    rating: doc.rating || 0,
    comment: doc.comment || '',
    date: formatDate(doc.created_at),
    myResponse: doc.vendor_response || null,
  };
}

function AvisCard({ avis, onEdit, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4"
    >
      <div className="flex gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          {avis.productImage ? (
            <img src={avis.productImage} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={24} className="text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{avis.productTitle}</h3>
          <p className="text-sm text-gray-500">{avis.seller}</p>
          <div className="flex items-center gap-1 mt-2">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={16}
                className={i < avis.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
              />
            ))}
            <span className="text-sm text-gray-500 ml-2">{avis.date}</span>
          </div>
        </div>
      </div>

      <p className="mt-3 text-gray-700">{avis.comment}</p>

      {avis.sellerResponse && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
          <p className="text-xs font-semibold text-blue-700 mb-1">Réponse du vendeur</p>
          <p className="text-sm text-blue-900">{avis.sellerResponse}</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <ThumbsUp size={14} />
            {avis.helpful} utile{avis.helpful > 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(avis)}>
            <Edit size={14} className="mr-1" /> Modifier
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onDelete(avis)}>
            <Trash2 size={14} className="mr-1" /> Supprimer
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AvisMarketplace({ onSelectTool }) {
  const [activeTab, setActiveTab] = useState('envoyes');

  const [avisEnvoyes, setAvisEnvoyes] = useState([]);
  const [avisRecus, setAvisRecus] = useState([]);
  const [avisEnAttente, setAvisEnAttente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals états
  const [showAvisModal, setShowAvisModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newAvisRating, setNewAvisRating] = useState(0);
  const [newAvisComment, setNewAvisComment] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAvis, setEditingAvis] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState('');
  const [editHoverRating, setEditHoverRating] = useState(0);

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyingToAvis, setReplyingToAvis] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      const [envRes, recusRes, pendingRes] = await Promise.all([
        fetch(`${API_URL}/api/boutique/me/avis-envoyes`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/boutique/me/avis-recus`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/boutique/me/pending-reviews`, { headers: authHeaders() }),
      ]);

      if (envRes.ok) {
        const data = await envRes.json();
        setAvisEnvoyes((data.avis || data || []).map(mapEnvoye));
      }
      if (recusRes.ok) {
        const data = await recusRes.json();
        setAvisRecus((data.avis || data || []).map(mapRecu));
      }
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setAvisEnAttente(data.pending || data || []);
      }
    } catch (err) {
      setError('Impossible de charger les avis.');
    } finally {
      setLoading(false);
    }
  }

  const handleOpenAvisModal = (product) => {
    setSelectedProduct(product);
    setNewAvisRating(0);
    setNewAvisComment('');
    setShowAvisModal(true);
  };

  const handleSubmitAvis = async () => {
    if (newAvisRating === 0 || !newAvisComment.trim() || !selectedProduct?.boutique_slug) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/boutique/${selectedProduct.boutique_slug}/avis`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          rating: newAvisRating,
          comment: newAvisComment,
          product_name: selectedProduct.boutique_name || selectedProduct.title,
          review_type: 'marketplace',
        }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setAvisEnvoyes(prev => [mapEnvoye(created), ...prev]);
      setAvisEnAttente(prev => prev.filter(p => p.boutique_slug !== selectedProduct.boutique_slug));
      setShowAvisModal(false);
    } catch {
      alert('Erreur lors de la publication de l\'avis.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (avisItem) => {
    setEditingAvis(avisItem);
    setEditRating(avisItem.rating);
    setEditComment(avisItem.comment);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (editRating === 0 || !editComment.trim() || !editingAvis?.boutique_slug) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/boutique/${editingAvis.boutique_slug}/avis/${editingAvis.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ rating: editRating, comment: editComment }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setAvisEnvoyes(prev => prev.map(a => a.id === editingAvis.id ? mapEnvoye(updated) : a));
      setShowEditModal(false);
      setEditingAvis(null);
    } catch {
      alert('Erreur lors de la modification.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (avisItem) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/boutique/${avisItem.boutique_slug}/avis/${avisItem.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      setAvisEnvoyes(prev => prev.filter(a => a.id !== avisItem.id));
    } catch {
      alert('Erreur lors de la suppression.');
    }
  };

  const handleOpenReply = (avisItem) => {
    setReplyingToAvis(avisItem);
    setReplyText(avisItem.myResponse || '');
    setShowReplyModal(true);
  };

  const handleSaveReply = async () => {
    if (!replyText.trim() || !replyingToAvis?.boutique_slug) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/boutique/${replyingToAvis.boutique_slug}/avis/${replyingToAvis.id}/response`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ response: replyText }),
      });
      if (!res.ok) throw new Error();
      setAvisRecus(prev => prev.map(a =>
        a.id === replyingToAvis.id ? { ...a, myResponse: replyText } : a
      ));
      setShowReplyModal(false);
      setReplyingToAvis(null);
    } catch {
      alert('Erreur lors de l\'enregistrement de la réponse.');
    } finally {
      setSubmitting(false);
    }
  };

  const avgRatingEnvoyes = avisEnvoyes.length > 0
    ? avisEnvoyes.reduce((acc, a) => acc + a.rating, 0) / avisEnvoyes.length
    : 0;

  const avgRatingRecus = avisRecus.length > 0
    ? avisRecus.reduce((acc, a) => acc + a.rating, 0) / avisRecus.length
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onSelectTool && (
          <button
            onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        )}
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Avis</h1>
          <p className="text-xs sm:text-sm text-gray-500">
            {activeTab === 'envoyes'
              ? `${avisEnvoyes.length} avis envoyé${avisEnvoyes.length > 1 ? 's' : ''}`
              : `${avisRecus.length} avis reçu${avisRecus.length > 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
          <button onClick={fetchAll} className="ml-auto underline text-xs">Réessayer</button>
        </div>
      )}

      {/* Onglets */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('envoyes')}
          className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'envoyes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Avis envoyés
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            activeTab === 'envoyes' ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-500'
          }`}>
            {avisEnvoyes.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('recus')}
          className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'recus' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Avis reçus
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            activeTab === 'recus' ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
          }`}>
            {avisRecus.length}
          </span>
        </button>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star size={24} className="text-yellow-500 fill-yellow-500" />
              <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                {activeTab === 'envoyes' ? avgRatingEnvoyes.toFixed(1) : avgRatingRecus.toFixed(1)}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500">Note moyenne</p>
          </div>
          <div className="flex-1">
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map(star => {
                const currentAvis = activeTab === 'envoyes' ? avisEnvoyes : avisRecus;
                const count = currentAvis.filter(a => a.rating === star).length;
                const percent = currentAvis.length > 0 ? (count / currentAvis.length) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-3">{star}</span>
                    <Star size={12} className="text-yellow-500 fill-yellow-500" />
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 rounded-full transition-all" style={{ width: `${percent}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-6">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Contenu selon l'onglet */}
      {activeTab === 'envoyes' ? (
        <>
          {/* Avis en attente */}
          {avisEnAttente.length > 0 && (
            <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
              <h3 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                <MessageCircle size={16} />
                Avis en attente ({avisEnAttente.length})
              </h3>
              <div className="space-y-3">
                {avisEnAttente.map(product => (
                  <div key={product.boutique_slug || product.id} className="flex items-center gap-3 bg-white rounded-lg p-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {product.image ? (
                        <img src={product.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={16} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.boutique_name || product.title}</p>
                      <p className="text-xs text-gray-500">{product.completed_at ? formatDate(product.completed_at) : ''}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleOpenAvisModal(product)}
                      className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
                    >
                      Donner mon avis
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liste des avis envoyés */}
          {avisEnvoyes.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Star size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun avis envoyé</h3>
              <p className="text-gray-500">Tu n'as pas encore laissé d'avis.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {avisEnvoyes.map(avisItem => (
                <AvisCard key={avisItem.id} avis={avisItem} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </>
      ) : (
        /* Avis reçus */
        <div className="space-y-4">
          {avisRecus.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Star size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun avis reçu</h3>
              <p className="text-gray-500">Tu n'as pas encore reçu d'avis sur tes ventes.</p>
            </div>
          ) : (
            avisRecus.map(avisItem => (
              <motion.div
                key={avisItem.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {avisItem.productImage ? (
                        <img src={avisItem.productImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={20} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">{avisItem.productTitle}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Avis de {avisItem.buyer}</p>
                      <div className="flex items-center gap-1 mt-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            size={14}
                            className={star <= avisItem.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-200'}
                          />
                        ))}
                        <span className="text-xs text-gray-500 ml-1">{avisItem.date}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">{avisItem.comment}</p>

                  {avisItem.myResponse && (
                    <div className="mt-4 bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                      <p className="text-xs font-semibold text-green-800 mb-1">Ta réponse</p>
                      <p className="text-sm text-green-700">{avisItem.myResponse}</p>
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => handleOpenReply(avisItem)} className="text-xs">
                    <MessageCircle size={14} className="mr-1" />
                    {avisItem.myResponse ? 'Modifier ma réponse' : 'Répondre'}
                  </Button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Modal Donner un avis */}
      <AnimatePresence>
        {showAvisModal && selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAvisModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Donner mon avis</h3>
                <button onClick={() => setShowAvisModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-14 h-14 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {selectedProduct.image ? (
                      <img src={selectedProduct.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={20} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{selectedProduct.boutique_name || selectedProduct.title}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Votre note</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setNewAvisRating(star)}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          size={32}
                          className={`${star <= (hoverRating || newAvisRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Votre avis</p>
                  <textarea
                    value={newAvisComment}
                    onChange={(e) => setNewAvisComment(e.target.value)}
                    placeholder="Partagez votre expérience..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button variant="outline" className="flex-1" onClick={() => setShowAvisModal(false)}>
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white"
                  onClick={handleSubmitAvis}
                  disabled={newAvisRating === 0 || !newAvisComment.trim() || submitting}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                  Publier mon avis
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Modifier un avis */}
      <AnimatePresence>
        {showEditModal && editingAvis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Modifier mon avis</h3>
                <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-14 h-14 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    {editingAvis.productImage ? (
                      <img src={editingAvis.productImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={20} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{editingAvis.productTitle}</p>
                    <p className="text-xs text-gray-500">{editingAvis.seller}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ta note</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setEditHoverRating(star)}
                        onMouseLeave={() => setEditHoverRating(0)}
                        onClick={() => setEditRating(star)}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          size={32}
                          className={`${star <= (editHoverRating || editRating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ton avis</p>
                  <textarea
                    value={editComment}
                    onChange={(e) => setEditComment(e.target.value)}
                    placeholder="Modifie ton avis..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={handleSaveEdit}
                  disabled={editRating === 0 || !editComment.trim() || submitting}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                  Enregistrer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Répondre à un avis reçu */}
      <AnimatePresence>
        {showReplyModal && replyingToAvis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] z-[9999] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowReplyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {replyingToAvis.myResponse ? 'Modifier ma réponse' : 'Répondre à l\'avis'}
                </h3>
                <button onClick={() => setShowReplyModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-semibold text-sm">
                        {replyingToAvis.buyer.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{replyingToAvis.buyer}</p>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            size={12}
                            className={star <= replyingToAvis.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-200'}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">{replyingToAvis.comment}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Ta réponse</p>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Remercie ton acheteur ou réponds à ses remarques..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button variant="outline" className="flex-1" onClick={() => setShowReplyModal(false)}>
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleSaveReply}
                  disabled={!replyText.trim() || submitting}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                  Enregistrer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
